import { ArrowRight, ArrowUpRight, Check } from "lucide-react";
import { Reveal, SectionHead } from "../components/Reveal";

const QUESTIONS = [
  "Where is the real demand?",
  "Who will actually buy?",
  "Which accounts should be approached first?",
  "Who could become a strategic partner?",
  "Who are the competitors?",
  "What could prevent successful entry?",
  "What should be invested, and when?",
  "What evidence is still missing before committing?",
];

const PROGRESSION = ["Market uncertainty", "Evidence", "Decision", "Execution"];

const CONVENTIONAL = [
  "Market size and growth",
  "Industry overviews",
  "Competitor lists",
  "Broad recommendations",
];

const CONNECTED = [
  "Buyer and account intelligence",
  "Customer segments and decision-makers",
  "Partner intelligence and competitive battlecards",
  "Buying triggers and pricing intelligence",
  "Regulatory gates and entry economics",
  "Risk scenarios and the first 10 meetings",
  "A practical 90-day execution blueprint",
  "A structured Intelligence Vault™",
];

const STAGES = [
  { n: "01", name: "Discover", desc: "India opportunity, market, segments, demand, and whitespace." },
  { n: "02", name: "Understand", desc: "Market, buyer, and competitor reality." },
  { n: "03", name: "Validate", desc: "Primary research, interviews, and assumptions." },
  { n: "04", name: "Decide", desc: "Entry model, economics, risk, and route to market." },
  { n: "05", name: "Activate", desc: "Priority accounts, partners, and first 90-day moves." },
];

export const QuestionSection = () => (
  <section id="the-decision" className="py-24 md:py-32" data-testid="section-decision">
    <div className="wrap">
      <SectionHead
        index="01 — The executive question"
        title={<>Market opportunity alone does not make a successful entry.</>}
        lead="A large, fast-growing market is not, by itself, a reason to commit capital. This engagement is built around the questions leadership must be able to answer before it does."
      />

      <div className="mt-16 border-y border-gold/12 md:mt-20">
        {QUESTIONS.map((q, i) => (
          <Reveal key={q} delay={i * 0.05} y={14}>
            <div className="group flex items-baseline gap-6 border-b border-gold/12 py-5 transition-all duration-300 last:border-b-0 hover:bg-abyss/40 hover:px-4 md:py-6">
              <span className="shrink-0 font-mono text-[11px] tracking-[0.2em] text-gold/70">
                Q.{String(i + 1).padStart(2, "0")}
              </span>
              <span className="font-display text-xl text-ivory/90 transition-colors duration-300 group-hover:text-ivory md:text-2xl">
                {q}
              </span>
              <ArrowUpRight
                size={16}
                aria-hidden="true"
                className="ml-auto shrink-0 self-center text-gold opacity-0 transition-opacity duration-300 group-hover:opacity-100"
              />
            </div>
          </Reveal>
        ))}
      </div>

      <Reveal delay={0.1} className="mt-16">
        <div className="flex flex-wrap items-center gap-4">
          {PROGRESSION.map((step, i) => (
            <span key={step} className="flex items-center gap-4">
              <span
                className={`border px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.24em] ${
                  step === "Evidence"
                    ? "border-gold/60 bg-gold/10 text-gold"
                    : "border-gold/20 text-mist"
                }`}
              >
                {step}
              </span>
              {i < PROGRESSION.length - 1 && (
                <ArrowRight size={14} className="text-gold/60" aria-hidden="true" />
              )}
            </span>
          ))}
        </div>
      </Reveal>
    </div>
  </section>
);

export const ConventionalSection = () => (
  <section id="conventional" className="py-24 md:py-32" data-testid="section-conventional">
    <div className="wrap">
      <SectionHead
        index="02 — Why conventional research is not enough"
        title={
          <>
            A report describes.
            <br />
            <em className="font-light text-gold-soft">A decision needs evidence.</em>
          </>
        }
      />

      <div className="mt-16 grid gap-6 md:mt-20 md:grid-cols-2">
        <Reveal>
          <div className="h-full border border-mist/15 bg-hull/40 p-8 transition-colors duration-300 hover:border-mist/30 md:p-10">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-mist/60">
              Conventional market research
            </p>
            <h3 className="mt-5 font-display text-2xl text-ivory/80">
              Describes the market.
            </h3>
            <ul className="mt-8 space-y-4">
              {CONVENTIONAL.map((item) => (
                <li key={item} className="flex items-start gap-4 text-[15px] text-mist">
                  <span className="mt-2.5 h-px w-5 shrink-0 bg-mist/40" aria-hidden="true" />
                  {item}
                </li>
              ))}
            </ul>
            <p className="mt-10 border-t border-mist/10 pt-6 text-sm italic leading-relaxed text-mist/70">
              Useful context. Rarely sufficient to commit capital, choose a route,
              or decide where to begin.
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.12}>
          <div className="relative h-full border border-gold/40 bg-hull2/60 p-8 transition-all duration-300 hover:border-gold/60 hover:shadow-[0_0_60px_rgba(201,162,75,0.08)] md:p-10">
            <span className="absolute right-0 top-0 bg-gold px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.22em] text-ink">
              Decision grade
            </span>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">
              The ENEXUR intelligence system
            </p>
            <h3 className="mt-5 font-display text-2xl text-ivory">
              Connects opportunity to evidence.
            </h3>
            <ul className="mt-8 space-y-4">
              {CONNECTED.map((item) => (
                <li key={item} className="flex items-start gap-4 text-[15px] text-ivory/85">
                  <Check size={14} className="mt-1.5 shrink-0 text-gold" aria-hidden="true" />
                  {item}
                </li>
              ))}
            </ul>
            <p className="mt-10 border-t border-gold/15 pt-6 text-sm italic leading-relaxed text-mist/80">
              Built to be acted on. Components are scoped to the engagement.
            </p>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

export const MethodSection = () => (
  <section id="system" className="py-24 md:py-32" data-testid="section-method">
    <div className="wrap">
      <SectionHead
        index="03 — The ENEXUR method"
        title={<>Five stages. One intelligence pathway.</>}
        lead="Each stage feeds the next. The output is not a document — it is a decision you can defend, and the first moves that follow it."
      />

      <div className="relative mt-20">
        <div
          className="absolute left-0 right-0 top-[5px] hidden h-px bg-gold/25 lg:block"
          aria-hidden="true"
        />
        <div className="grid gap-14 lg:grid-cols-5 lg:gap-8">
          {STAGES.map((s, i) => (
            <Reveal key={s.n} delay={i * 0.12}>
              <div className="lg:pt-0">
                <div className="flex items-center gap-4 lg:block">
                  <span
                    className="relative z-10 block h-[11px] w-[11px] rotate-45 border border-gold bg-ink lg:mb-9"
                    aria-hidden="true"
                  />
                  <span className="font-mono text-[10px] tracking-[0.3em] text-gold">
                    Stage {s.n}
                  </span>
                </div>
                <h3 className="mt-3 font-display text-2xl text-ivory lg:mt-0">{s.name}</h3>
                <p className="mt-3 text-[15px] leading-relaxed text-mist">{s.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  </section>
);
